import { getAttachmentsByDocId } from "@/api/attachment";
import { getDefaultImage, TypeDefaultImage } from "@/lib/image";
import { Attachment } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { EyeIcon } from "lucide-react";
import { ReactNode, useMemo } from "react";
import StyledDrawer from "../StyledDrawer";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";

interface ShowElementsProps {
  elements: {
    label: string;
    value: string | Attachment[];
    type: "text" | "richtext" | "image";
  }[];
  onAdd?: () => void;
  id?: string;
  type: TypeDefaultImage;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
}

const ShowElements = ({
  elements,
  onAdd,
  id,
  type,
  open,
  onOpenChange,
  trigger,
}: ShowElementsProps) => {
  const elementsToShow = elements.filter((element) => element.type !== "image");
  const imagesToShow = elements.filter((element) => element.type === "image");

  const { data: _images } = useQuery({
    queryKey: ["attachments", id],
    queryFn: () => getAttachmentsByDocId(id ?? ""),
    enabled: !!id && imagesToShow.length > 0 && imagesToShow[0].value !== null,
  });

  const image = useMemo(() => {
    if (imagesToShow.length === 0) return null;
    if (imagesToShow[0].value) {
      return getDefaultImage(
        imagesToShow[0].value as Attachment[],
        type,
        "mediumPublicUrl",
      );
    } else {
      return getDefaultImage(_images ?? [], type, "mediumPublicUrl");
    }
  }, [imagesToShow, _images, type]);

  const isControlled = open !== undefined || onOpenChange !== undefined;
  const resolvedTrigger =
    trigger !== undefined
      ? trigger
      : isControlled
        ? undefined
        : (
            <Button
              className="font-semibold flex gap-2 p-1"
              variant="ghost"
              type="button"
            >
              <EyeIcon className="w-5 h-5 cursor-pointer text-gray-500" />
            </Button>
          );

  return (
    <StyledDrawer
      sheetProps={{ open, onOpenChange }}
      title={""}
      className="flex flex-col justify-between overflow-y-auto"
      footer={
        onAdd ? (
          <CustomDialogFooter>
            <Button className="w-full" type="button" onClick={onAdd}>
              Ajouter
            </Button>
          </CustomDialogFooter>
        ) : null
      }
      trigger={resolvedTrigger}
    >
      {image && (
        <div>
          {imagesToShow.map((element) => (
            <img
              src={image ?? ""}
              alt={element.label}
              className="w-full h-full object-cover"
            />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-4 mt-4">
        {elementsToShow.map((element) => (
          <div className="flex flex-col">
            <p className="font-[500] font-['Raleway'] text-sm text-gray-700">
              {element.label}
            </p>
            {element.type === "text" && (
              <p className="font-[500] font-['Raleway'] text-[16px] ">
                {element.value as string}
              </p>
            )}
            {element.type === "richtext" && (
              <div
                className="font-[500] font-['Raleway'] text-[16px]"
                dangerouslySetInnerHTML={{ __html: element.value as string }}
              ></div>
            )}
          </div>
        ))}
      </div>
    </StyledDrawer>
  );
};

export default ShowElements;
